import { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Layout } from "../components/Layout";
import api from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Switch } from "../components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { usePopup } from "../components/AppPopups";
import { toast } from "sonner";
import { fmtDate } from "../lib/format";
import { useAuth } from "../lib/auth";
import { hasAnyPermission, hasPermission, permissionLabel } from "../lib/permissions";
import { PLAN_FEATURES, hasPlanFeature } from "../lib/planFeatures";
import { ArrowLeft, Plus, Trash2, Pencil, Users, MapPin, Building2, Eye, HelpCircle, Search, SlidersHorizontal } from "lucide-react";
import { DataPanel, EmptyState, MetricStrip, StatusPill, TableSurface } from "../components/CommandSystem";

const PERMISSION_GROUPS = [
  {
    title: "Inventory",
    summary: "Hoardings, availability calendar, photos, rent fields, and site records.",
    items: [
      { key: "view_hoardings", label: "View hoardings", affects: ["Hoardings", "Availability", "Hoarding detail", "Proposal inventory picker"], help: "Lets the employee see hoarding inventory within their assigned scope." },
      { key: "create_hoardings", label: "Create hoardings", affects: ["Hoardings", "Add hoarding"], help: "Shows the add-hoarding action and allows new site records." },
      { key: "edit_own_hoardings", label: "Edit hoardings created by them", affects: ["Hoarding detail", "Edit hoarding"], help: "Allows edits only on hoardings this employee created." },
      { key: "edit_assigned_hoardings", label: "Edit assigned hoardings", affects: ["Hoarding detail", "Edit hoarding"], help: "Allows edits on hoardings inside their city, area, or assigned-hoarding scope." },
      { key: "edit_all_hoardings", label: "Edit all hoardings", affects: ["Hoarding detail", "Edit hoarding"], help: "Allows edits on every hoarding in the advertiser workspace." },
      { key: "upload_hoarding_photos", label: "Upload hoarding photos", affects: ["Hoarding form", "Hoarding photos"], help: "Allows adding or changing hoarding photo assets." },
      { key: "update_hoarding_availability", label: "Update availability", affects: ["Availability calendar", "Hoarding schedule"], help: "Allows blocking dates, maintenance entries, and availability changes." },
      { key: "delete_hoardings", label: "Delete hoardings", affects: ["Hoarding detail"], help: "Allows removing hoarding records." },
    ],
  },
  {
    title: "Campaigns",
    summary: "Bookings, renewals, campaign dates, assigned hoardings, and campaign amounts.",
    items: [
      { key: "view_own_campaigns", label: "View own campaigns", affects: ["Campaigns", "Campaign detail"], help: "Shows campaigns created by this employee." },
      { key: "view_assigned_campaigns", label: "View assigned campaigns", affects: ["Campaigns", "Campaign detail"], help: "Shows campaigns connected to their assigned inventory or branch scope." },
      { key: "view_all_campaigns", label: "View all campaigns", affects: ["Campaigns", "Renewal queue", "Campaign KPIs"], help: "Shows all workspace campaigns." },
      { key: "create_campaigns", label: "Create campaigns", affects: ["Campaigns", "Create campaign", "Proposal generation"], help: "Allows creating bookings and also unlocks several proposal workflows." },
      { key: "edit_own_campaigns", label: "Edit campaigns created by them", affects: ["Campaign detail"], help: "Allows edits only on campaigns this employee created." },
      { key: "edit_assigned_campaigns", label: "Edit assigned campaigns", affects: ["Campaign detail"], help: "Allows edits on campaigns in their assigned scope." },
      { key: "edit_all_campaigns", label: "Edit all campaigns", affects: ["Campaign detail"], help: "Allows edits on every campaign in the workspace." },
      { key: "assign_hoardings_to_campaign", label: "Assign hoardings to campaigns", affects: ["Campaign form", "Campaign hoarding picker"], help: "Allows changing which hoardings are attached to a campaign." },
      { key: "upload_campaign_proofs", label: "Upload campaign proof photos", affects: ["Campaign detail", "Campaign audit"], help: "Allows uploading dated proof photos for hoardings assigned to a campaign." },
      { key: "change_campaign_dates", label: "Change campaign dates", affects: ["Campaign form", "Renew campaign"], help: "Allows changing booking start/end dates and renewals." },
      { key: "change_campaign_amounts", label: "Change campaign amounts", affects: ["Campaign form", "Business margins", "Finance"], help: "Allows changing campaign charge and PO amount fields." },
      { key: "delete_campaigns", label: "Delete campaigns", affects: ["Campaigns"], help: "Allows removing campaign records." },
    ],
  },
  {
    title: "Clients",
    summary: "Client list, contacts, client-owned records, and revenue visibility.",
    items: [
      { key: "view_own_clients", label: "View own clients", affects: ["Clients", "Campaign client selector"], help: "Shows clients created by this employee." },
      { key: "view_assigned_clients", label: "View assigned clients", affects: ["Clients", "Campaign client selector"], help: "Shows clients connected to assigned campaigns or inventory scope." },
      { key: "view_all_clients", label: "View all clients", affects: ["Clients", "Client analytics"], help: "Shows every client in the workspace." },
      { key: "create_clients", label: "Create clients", affects: ["Clients", "Campaign form"], help: "Allows adding new client records." },
      { key: "edit_own_clients", label: "Edit clients created by them", affects: ["Clients"], help: "Allows editing only clients this employee created." },
      { key: "edit_assigned_clients", label: "Edit assigned clients", affects: ["Clients"], help: "Allows editing clients in their assigned scope." },
      { key: "edit_all_clients", label: "Edit all clients", affects: ["Clients"], help: "Allows editing every client in the workspace." },
      { key: "view_client_contact_details", label: "View client contact details", affects: ["Clients", "Campaign detail", "Hoarding bookings"], help: "Reveals mobile/email/contact fields that are hidden from restricted employees." },
      { key: "delete_clients", label: "Delete clients", affects: ["Clients"], help: "Allows removing client records." },
    ],
  },
  {
    title: "Finance Work Queue",
    summary: "Receivables, rent payables, expense rows, payment actions, and work queue KPIs.",
    items: [
      { key: "view_basic_revenue", label: "View basic revenue", affects: ["Dashboard", "Work Queue receivables", "Analysis"], help: "Shows high-level revenue numbers without full profit/loss detail." },
      { key: "view_campaign_amounts", label: "View campaign amounts", affects: ["Campaigns", "Work Queue receivables", "Campaign finance"], help: "Shows campaign amount, PO, invoice, and receivable values." },
      { key: "edit_campaign_amounts", label: "Edit campaign amounts", affects: ["Campaign form", "Business margins"], help: "Allows editing campaign amount fields and business margin settings." },
      { key: "view_rent_details", label: "View rent details", affects: ["Work Queue rent payables", "Hoarding financials"], help: "Shows landowner rent, due dates, rent payable queue, and rent costs." },
      { key: "edit_rent_details", label: "Edit rent details", affects: ["Hoarding form", "Work Queue rent paid action"], help: "Allows rent field edits and marking rent as paid." },
      { key: "view_expenses", label: "View expenses", affects: ["Hoarding financials", "Work Queue rent events"], help: "Shows expense rows and cost-side operational data." },
      { key: "create_expenses", label: "Create expenses", affects: ["Hoarding financials", "Work Queue rent paid action"], help: "Allows creating expense entries, including rent payment records." },
      { key: "edit_expenses", label: "Edit expenses", affects: ["Expenses", "Work Queue rent paid action"], help: "Allows updating expense-related payment data." },
      { key: "delete_expenses", label: "Delete expenses", affects: ["Hoarding financials"], help: "Allows deleting expense rows." },
      { key: "view_profit_and_loss", label: "View P&L", affects: ["Dashboard", "Analysis", "Work Queue", "Reports"], help: "Shows profit/loss, rent cost, expense cost, and net margin data." },
      { key: "view_financial_reports", label: "View financial reports", affects: ["Reports", "Analysis", "Accounts read-only", "Audit events"], help: "Broad read access for financial reports and related accounting summaries." },
      { key: "export_reports", label: "Export reports", affects: ["Reports", "Proposals", "Audit events"], help: "Allows exporting report and proposal files." },
    ],
  },
  {
    title: "Accounts",
    summary: "Invoices, payments, payables, GST summary, Tally export, and accounting periods.",
    items: [
      { key: "view_accounts", label: "View accounts", affects: ["Accounts", "Ledger mappings", "Aging reports"], help: "Allows read-only access to accounting settings, ledgers, receivables, and payables." },
      { key: "manage_accounts", label: "Manage accounts", affects: ["Accounts", "Adjustments", "Periods", "Ledger mappings"], help: "Allows managing accounting settings, adjustments, periods, and ledger mappings." },
      { key: "view_invoices", label: "View invoices", affects: ["Accounts invoices"], help: "Shows invoice list and invoice detail." },
      { key: "manage_invoices", label: "Manage invoices", affects: ["Accounts invoices"], help: "Allows creating and updating invoices." },
      { key: "view_payments", label: "View payments", affects: ["Accounts payments"], help: "Shows received payment records." },
      { key: "manage_payments", label: "Manage payments", affects: ["Accounts payments", "Payable payments"], help: "Allows recording and reversing payments." },
      { key: "view_export_templates", label: "View export templates", affects: ["Tally export templates"], help: "Shows export template setup." },
      { key: "manage_export_templates", label: "Manage export templates", affects: ["Tally export templates"], help: "Allows creating, duplicating, editing, and deleting export templates." },
      { key: "export_accounts_data", label: "Export accounts data", affects: ["Tally export", "Trial balance", "Aging reports"], help: "Allows previewing and downloading accounting exports." },
      { key: "view_gst_summary", label: "View GST summary", affects: ["Accounts GST summary"], help: "Shows GST summary data." },
    ],
  },
  {
    title: "Proposals",
    summary: "Proposal builder, sent proposals, templates, exports, arrangement flows, and partner inventory.",
    items: [
      { key: "view_proposal_flows", label: "View proposal flows", affects: ["Proposal arrangements"], help: "Shows saved proposal arrangement presets." },
      { key: "manage_proposal_flows", label: "Manage proposal flows", affects: ["Proposal arrangements"], help: "Allows creating, editing, and deleting proposal arrangement presets." },
      { key: "manage_partner_network", label: "Manage Partner Network", affects: ["Partner Network", "Business settings"], help: "Allows showing Partner Network in the sidebar, sharing access codes, partner connections, inventory offers, and approvals." },
    ],
  },
  {
    title: "Analytics & AI",
    summary: "City analytics, insights, trends, Ask AI, and intelligence recommendations.",
    items: [
      { key: "view_city_analytics", label: "View city analytics", affects: ["City analytics", "Analytics city trends"], help: "Allows city-level analytics and trend endpoints." },
      { key: "view_ai_insights", label: "View AI insights", affects: ["Insights", "Ask AI", "Business intelligence"], help: "Allows AI insight surfaces. Employees still need all-inventory scope for some AI features." },
    ],
  },
  {
    title: "Team",
    summary: "Employee creation, permission management, branches, inventory assignment, and admin audit access.",
    items: [
      { key: "invite_employees", label: "Invite employees", affects: ["Employees", "Branches"], help: "Allows adding employees and opening team setup areas." },
      { key: "edit_employee_permissions", label: "Edit employee permissions", affects: ["Employees", "Permission requests", "Branches"], help: "Allows changing employee permissions and approving permission requests." },
      { key: "view_audit_logs", label: "View audit logs", affects: ["Audit Logs"], help: "Allows viewing the last 15 days of platform activity logs." },
      { key: "deactivate_employees", label: "Deactivate employees", affects: ["Employees"], help: "Allows disabling or removing employee access." },
      { key: "assign_inventory_to_employees", label: "Assign inventory to employees", affects: ["Employees", "Inventory assignment"], help: "Allows assigning employees to all inventory, cities, areas, or specific hoardings." },
    ],
  },
];

const ALL_PERMISSIONS = PERMISSION_GROUPS.flatMap((g) => g.items);
const DEFAULT_PERMISSIONS = Object.fromEntries(ALL_PERMISSIONS.map((p) => [p.key, false]));
const DEFAULT_PROFILE = { role_preset: "sales_executive", scope_type: "assigned_cities", cities: [], areas: [], hoarding_ids: [] };
const BRANCH_TYPE_LABELS = { main: "Main Branch", local: "Local Branch", agency: "Agency Branch" };

const ROLE_PRESETS = [
  {
    id: "sales_executive",
    label: "Sales Executive",
    desc: "Assigned market, clients, own campaigns, no rent/P&L.",
    scope_type: "assigned_cities",
    permissions: [
      "view_hoardings", "view_own_campaigns", "view_assigned_campaigns", "create_campaigns",
      "edit_own_campaigns", "assign_hoardings_to_campaign", "upload_campaign_proofs", "change_campaign_dates",
      "create_clients", "view_own_clients", "view_assigned_clients", "edit_own_clients",
      "view_client_contact_details", "view_basic_revenue", "view_campaign_amounts",
    ],
  },
  {
    id: "operations_executive",
    label: "Operations Executive",
    desc: "Assigned inventory, site updates, photos, availability.",
    scope_type: "assigned_hoardings",
    permissions: [
      "view_hoardings", "create_hoardings", "edit_assigned_hoardings", "upload_hoarding_photos",
      "update_hoarding_availability", "view_assigned_campaigns", "upload_campaign_proofs",
    ],
  },
  {
    id: "city_manager",
    label: "City Manager",
    desc: "Can manage a city/area team and see revenue, not full P&L.",
    scope_type: "assigned_cities",
    permissions: [
      "view_hoardings", "create_hoardings", "edit_assigned_hoardings", "upload_hoarding_photos",
      "update_hoarding_availability", "view_assigned_campaigns", "view_all_campaigns",
      "create_campaigns", "edit_assigned_campaigns", "assign_hoardings_to_campaign", "upload_campaign_proofs",
      "change_campaign_dates", "view_assigned_clients", "view_all_clients", "create_clients",
      "edit_assigned_clients", "view_client_contact_details", "view_basic_revenue",
      "view_campaign_amounts", "view_city_analytics", "export_reports",
    ],
  },
  {
    id: "proposal_coordinator",
    label: "Proposal Coordinator",
    desc: "Inventory visibility, proposal exports, templates, and arrangement flows.",
    scope_type: "all_inventory",
    permissions: [
      "view_hoardings", "create_campaigns", "export_reports", "view_proposal_flows", "manage_proposal_flows",
      "view_all_clients", "view_client_contact_details", "view_basic_revenue", "view_campaign_amounts",
    ],
  },
  {
    id: "finance_admin",
    label: "Finance/Admin",
    desc: "Expenses, rent, reports, amounts; no inventory deletion by default.",
    scope_type: "all_inventory",
    permissions: [
      "view_hoardings", "view_all_campaigns", "view_all_clients", "view_client_contact_details",
      "view_basic_revenue", "view_campaign_amounts", "edit_campaign_amounts", "view_rent_details",
      "edit_rent_details", "view_expenses", "create_expenses", "edit_expenses", "delete_expenses",
      "view_profit_and_loss", "view_financial_reports", "export_reports", "view_ai_insights",
    ],
  },
  {
    id: "accountant",
    label: "Accountant",
    desc: "Invoices, payments, payables, GST, exports, and finance work queue.",
    scope_type: "all_inventory",
    permissions: [
      "view_hoardings", "view_all_campaigns", "view_all_clients", "view_client_contact_details",
      "view_basic_revenue", "view_campaign_amounts", "view_rent_details", "view_expenses",
      "view_financial_reports", "view_accounts", "manage_accounts", "view_invoices", "manage_invoices",
      "view_payments", "manage_payments", "view_export_templates", "manage_export_templates",
      "export_accounts_data", "view_gst_summary",
    ],
  },
  {
    id: "team_admin",
    label: "Team Admin",
    desc: "Employee access, branches, scopes, and permission requests.",
    scope_type: "all_inventory",
    permissions: [
      "view_hoardings", "invite_employees", "edit_employee_permissions", "deactivate_employees",
      "assign_inventory_to_employees", "view_financial_reports", "export_reports",
    ],
  },
];

function withLegacyAliases(perms) {
  const out = { ...DEFAULT_PERMISSIONS, ...perms };
  out.create_clients = !!out.create_clients;
  out.create_hoardings = !!out.create_hoardings;
  out.create_campaigns = !!out.create_campaigns;
  out.modify_records = [
    "edit_own_hoardings", "edit_assigned_hoardings", "edit_all_hoardings",
    "edit_own_campaigns", "edit_assigned_campaigns", "edit_all_campaigns",
    "edit_own_clients", "edit_assigned_clients", "edit_all_clients",
    "update_hoarding_availability", "upload_hoarding_photos", "upload_campaign_proofs",
  ].some((key) => out[key]);
  out.delete_records = ["delete_hoardings", "delete_campaigns", "delete_clients"].some((key) => out[key]);
  out.view_pnl = !!(out.view_profit_and_loss || out.view_financial_reports);
  return out;
}

function profileFrom(value) {
  return { ...DEFAULT_PROFILE, ...(value || {}) };
}

export function Employees() {
  const { user } = useAuth();
  const nav = useNavigate();
  const popup = usePopup();
  const [employees, setEmployees] = useState([]);
  const [permissionRequests, setPermissionRequests] = useState([]);
  const [hoardings, setHoardings] = useState([]);
  const [branches, setBranches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editTarget, setEditTarget] = useState(null);
  const canInvite = hasPermission(user, "invite_employees");
  const canEdit = hasPlanFeature(user, PLAN_FEATURES.employeeAccess) && hasAnyPermission(user, ["edit_employee_permissions", "deactivate_employees", "assign_inventory_to_employees"]);
  const canDeactivate = hasPermission(user, "deactivate_employees");

  const fetchEmployees = () => {
    setLoading(true);
    api.get("/employees", { skipGlobalLoader: true })
      .then(({ data }) => setEmployees(data || []))
      .catch(() => toast.error("Failed to load employees"))
      .finally(() => setLoading(false));
  };

  const fetchPermissionRequests = () => {
    if (!canEdit) return;
    api.get("/permission-requests", { skipGlobalLoader: true })
      .then(({ data }) => setPermissionRequests(Array.isArray(data) ? data : []))
      .catch(() => setPermissionRequests([]));
  };

  useEffect(() => {
    fetchEmployees();
    fetchPermissionRequests();
    api.get("/hoardings", { skipGlobalLoader: true }).then(({ data }) => setHoardings(data || [])).catch(() => setHoardings([]));
    api.get("/branches", { skipGlobalLoader: true }).then(({ data }) => setBranches(data || [])).catch(() => setBranches([]));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const pendingRequests = permissionRequests.filter((req) => req.status === "pending");
  const activeEmployees = employees.filter((emp) => emp.status === "active").length;

  function viewRequest(req) {
    const employee = employees.find((emp) => emp.id === req.employee_id);
    if (employee) setEditTarget({ ...employee, permission_request: req });
  }

  async function handleDelete(emp) {
    const ok = await popup.confirm({
      title: `Remove ${emp.name}?`,
      description: "This employee will no longer be part of your team.",
      confirmText: "Remove Employee",
      tone: "danger",
    });
    if (!ok) return;
    try {
      await api.delete(`/employees/${emp.id}`);
      toast.success("Employee removed");
      fetchEmployees();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to delete");
    }
  }

  return (
    <Layout
      title="Employees"
      subtitle="Assign roles, branch scope, inventory access, and production permissions from one command surface."
      actions={canInvite && (
        <Button onClick={() => nav("/employees/new", { state: { from: "/employees" } })} className="bg-blue-600 hover:bg-blue-700 text-white gap-1.5">
          <Plus size={15} /> Add Employee
        </Button>
      )}
    >
      <div className="hx-employees-page space-y-5">
        <MetricStrip
          metrics={[
            { label: "Team members", value: employees.length, hint: `${activeEmployees} active`, tone: "blue", icon: Users },
            { label: "Permission requests", value: pendingRequests.length, hint: pendingRequests.length ? "Pending approval" : "Nothing pending", tone: pendingRequests.length ? "amber" : "emerald" },
            { label: "Branches", value: branches.length, hint: "Available assignment scopes", tone: "indigo", icon: Building2 },
          ]}
        />

        {pendingRequests.length > 0 && (
          <DataPanel
            className="hx-employee-panel"
            title="Permission requests"
            subtitle="Review requested access and approve from the employee editor."
            actions={<span className="rounded-full bg-amber-100 px-2 py-1 text-xs font-bold text-amber-800">{pendingRequests.length}</span>}
          >
            <div className="grid gap-2">
              {pendingRequests.map((req) => (
                <div key={req.id} className="flex flex-col gap-2 rounded-md bg-white/80 p-3 sm:flex-row sm:items-center sm:justify-between">
                  <div className="min-w-0">
                    <div className="text-sm font-semibold text-slate-900">{req.employee_name} requested {permissionLabel(req.requested_permission)}</div>
                    <div className="truncate text-xs text-slate-500">{req.note || "No note added"}</div>
                  </div>
                  <Button size="sm" variant="outline" onClick={() => viewRequest(req)}>
                    <Eye size={14} /> View
                  </Button>
                </div>
              ))}
            </div>
          </DataPanel>
        )}

        <DataPanel
          className="hx-employee-panel hx-employee-directory-panel"
          title="Team directory"
          subtitle="Role, branch, status, and permission scope for every employee."
          actions={
            <div className="flex items-center gap-2 text-xs font-medium text-slate-500">
              <Users size={14} />
              <span>{employees.length} team member{employees.length !== 1 ? "s" : ""}</span>
            </div>
          }
        >
          {loading ? (
            <EmptyState title="Loading employees" description="Fetching team access records..." />
          ) : employees.length === 0 ? (
            <EmptyState title="No employees yet" description="Add your first team member to assign scoped permissions." />
          ) : (
            <TableSurface className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-slate-100 bg-slate-50 text-xs font-semibold text-slate-500 uppercase tracking-wide">
                    <th className="px-5 py-3 text-left">Name</th>
                    <th className="px-5 py-3 text-left">Role & Scope</th>
                    <th className="px-5 py-3 text-left">Branch</th>
                    <th className="px-5 py-3 text-left">Status</th>
                    <th className="px-5 py-3 text-left">Access</th>
                    <th className="px-5 py-3 text-left">Added</th>
                    <th className="px-5 py-3 text-right">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-50">
                  {employees.map((emp) => (
                    <tr key={emp.id} className="hover:bg-slate-50/60 transition-colors">
                      <td className="px-5 py-3">
                        <div className="font-medium text-slate-800">{emp.name}</div>
                        <div className="text-xs text-slate-500">{emp.email}</div>
                      </td>
                      <td className="px-5 py-3">
                        <ProfileSummary profile={emp.access_profile} />
                      </td>
                      <td className="px-5 py-3">
                        <BranchBadge employee={emp} branches={branches} />
                      </td>
                      <td className="px-5 py-3">
                        <StatusPill tone={emp.status === "active" ? "emerald" : "slate"}>{emp.status}</StatusPill>
                      </td>
                      <td className="px-5 py-3 max-w-md">
                        <PermissionBadges permissions={emp.permissions || {}} />
                      </td>
                      <td className="px-5 py-3 text-slate-400">{fmtDate(emp.created_at)}</td>
                      <td className="px-5 py-3 text-right">
                        <div className="flex items-center justify-end gap-2">
                          {canEdit && <button onClick={() => setEditTarget(emp)} className="p-1.5 rounded-lg text-slate-400 hover:text-blue-600 hover:bg-blue-50 transition-colors" title="Edit access">
                            <Pencil size={14} />
                          </button>}
                          {canDeactivate && <button onClick={() => handleDelete(emp)} className="p-1.5 rounded-lg text-slate-400 hover:text-rose-600 hover:bg-rose-50 transition-colors" title="Remove employee">
                            <Trash2 size={14} />
                          </button>}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </TableSurface>
          )}
        </DataPanel>
      </div>

      {editTarget && (
        <EditEmployeeDialog
          employee={editTarget}
          hoardings={hoardings}
          branches={branches}
          onClose={() => setEditTarget(null)}
          requestedPermission={editTarget.permission_request?.requested_permission}
          permissionRequest={editTarget.permission_request}
          onSaved={() => { setEditTarget(null); fetchEmployees(); fetchPermissionRequests(); }}
        />
      )}
    </Layout>
  );
}

function ProfileSummary({ profile }) {
  const p = profileFrom(profile);
  const role = ROLE_PRESETS.find((r) => r.id === p.role_preset)?.label || "Custom";
  const scopeLabel = {
    all_inventory: "All inventory",
    assigned_cities: `${p.cities?.length || 0} cities`,
    assigned_areas: `${p.areas?.length || 0} areas`,
    assigned_hoardings: `${p.hoarding_ids?.length || 0} hoardings`,
  }[p.scope_type] || "Custom scope";

  return (
    <div>
      <div className="font-medium text-slate-800">{role}</div>
      <div className="mt-1 inline-flex items-center gap-1 text-xs text-slate-500">
        <MapPin size={12} /> {scopeLabel}
      </div>
    </div>
  );
}

function BranchBadge({ employee, branches }) {
  const branch = branches.find((item) => item.id === employee.branch_id) || branches.find((item) => item.branch_type === employee.branch_type);
  const branchType = branch?.branch_type || employee.branch_type || "main";
  return (
    <div>
      <div className="font-medium text-slate-800">{branch?.name || BRANCH_TYPE_LABELS[branchType] || "Main Branch"}</div>
      <div className="mt-1 text-xs text-slate-400">{BRANCH_TYPE_LABELS[branchType] || "Branch"}</div>
    </div>
  );
}

function PermissionBadges({ permissions }) {
  const granted = ALL_PERMISSIONS.filter((p) => permissions[p.key]);
  if (granted.length === 0) return <span className="text-xs text-slate-400 italic">No operational access</span>;
  return (
    <div className="flex flex-wrap gap-1">
      {granted.slice(0, 8).map((p) => (
        <span key={p.key} className="px-1.5 py-0.5 rounded text-[10px] font-medium bg-blue-50 text-blue-700">{p.label}</span>
      ))}
      {granted.length > 8 && <span className="px-1.5 py-0.5 rounded text-[10px] font-medium bg-slate-100 text-slate-600">+{granted.length - 8} more</span>}
    </div>
  );
}

export function CreateEmployeePage() {
  const { user } = useAuth();
  const nav = useNavigate();
  const location = useLocation();
  const [hoardings, setHoardings] = useState([]);
  const [branches, setBranches] = useState([]);
  const [loadingLookups, setLoadingLookups] = useState(true);
  const [form, setForm] = useState({ name: "", email: "", password: "" });
  const [branchSelection, setBranchSelection] = useState({ branch_id: "", branch_type: "main" });
  const [permissions, setPermissions] = useState({ ...DEFAULT_PERMISSIONS });
  const [profile, setProfile] = useState({ ...DEFAULT_PROFILE });
  const [saving, setSaving] = useState(false);
  const canEditPermissions = hasPlanFeature(user, PLAN_FEATURES.employeeAccess) && hasPermission(user, "edit_employee_permissions");
  const canAssignInventory = hasPlanFeature(user, PLAN_FEATURES.employeeAccess) && hasPermission(user, "assign_inventory_to_employees");
  const backTo = location.state?.from || "/employees";

  useEffect(() => {
    setLoadingLookups(true);
    Promise.allSettled([
      api.get("/hoardings", { skipGlobalLoader: true }),
      api.get("/branches", { skipGlobalLoader: true }),
    ]).then(([hoardingRes, branchRes]) => {
      setHoardings(hoardingRes.status === "fulfilled" ? (hoardingRes.value.data || []) : []);
      const nextBranches = branchRes.status === "fulfilled" ? (branchRes.value.data || []) : [];
      setBranches(nextBranches);
      setBranchSelection(defaultBranchSelection(nextBranches));
    }).finally(() => setLoadingLookups(false));
  }, []);

  function goBack() {
    nav(backTo, { replace: true });
  }

  async function handleSubmit(e) {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = {
        ...form,
        ...branchSelection,
        permissions: withLegacyAliases(permissions),
        access_profile: profile,
      };
      await api.post("/employees", payload);
      toast.success("Employee created");
      goBack();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to create employee");
    } finally {
      setSaving(false);
    }
  }

  return (
    <Layout title="Add Employee" subtitle="Create a team member with branch, inventory, and permission access." mainClassName="p-4 md:p-5">
      <form onSubmit={handleSubmit} className="hx-employee-access space-y-4">
        <div className="hx-employee-action-bar flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
          <button
            type="button"
            onClick={goBack}
            className="inline-flex items-center gap-2 text-sm font-semibold text-slate-600 transition-colors hover:text-slate-950"
          >
            <ArrowLeft size={16} /> Back to employees
          </button>
          <div className="flex flex-wrap items-center gap-2">
            <Button type="button" variant="outline" onClick={goBack} disabled={saving}>Cancel</Button>
            <Button type="submit" disabled={saving || loadingLookups} className="bg-blue-600 hover:bg-blue-700">
              {saving ? "Creating..." : "Create Employee"}
            </Button>
          </div>
        </div>

        <DataPanel title="Employee details" subtitle="Basic login identity and default branch access." className="hx-employee-panel">
          <div className="space-y-3">
            <EmployeeIdentity form={form} setForm={setForm} />
            <BranchSelector branches={branches} value={branchSelection} onChange={setBranchSelection} />
          </div>
        </DataPanel>

        <DataPanel title="Access setup" subtitle="Use a preset, then tune permissions and inventory scope." className="hx-employee-panel">
          {loadingLookups ? (
            <EmptyState title="Loading access data" description="Fetching branches and hoardings for assignment." />
          ) : (
            <AccessDesigner
              hoardings={hoardings}
              permissions={permissions}
              setPermissions={setPermissions}
              profile={profile}
              setProfile={setProfile}
              canEditPermissions={canEditPermissions}
              canAssignInventory={canAssignInventory}
            />
          )}
        </DataPanel>
      </form>
    </Layout>
  );
}

function CreateEmployeeDialog({ open, hoardings, branches, canEditPermissions, canAssignInventory, onClose, onCreated }) {
  const [form, setForm] = useState({ name: "", email: "", password: "" });
  const [branchSelection, setBranchSelection] = useState({ branch_id: "", branch_type: "main" });
  const [permissions, setPermissions] = useState({ ...DEFAULT_PERMISSIONS });
  const [profile, setProfile] = useState({ ...DEFAULT_PROFILE });
  const [saving, setSaving] = useState(false);

  function reset() {
    setForm({ name: "", email: "", password: "" });
    setBranchSelection(defaultBranchSelection(branches));
    setPermissions({ ...DEFAULT_PERMISSIONS });
    setProfile({ ...DEFAULT_PROFILE });
  }

  useEffect(() => {
    if (open) setBranchSelection(defaultBranchSelection(branches));
  }, [open, branches]);

  async function handleSubmit(e) {
    e.preventDefault();
    if (!form.name || !form.email || !form.password) return toast.error("Name, email and password are required");
    setSaving(true);
    try {
      const payload = { ...form };
      if (branchSelection.branch_type) payload.branch_type = branchSelection.branch_type;
      if (branchSelection.branch_id) payload.branch_id = branchSelection.branch_id;
      if (canEditPermissions) payload.permissions = withLegacyAliases(permissions);
      if (canAssignInventory) payload.access_profile = profile;
      await api.post("/employees", payload);
      toast.success("Employee created");
      reset();
      onCreated();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to create employee");
    } finally {
      setSaving(false);
    }
  }

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) { reset(); onClose(); } }}>
      <DialogContent className="max-w-5xl max-h-[90vh] overflow-y-auto">
        <DialogHeader><DialogTitle>Add Employee</DialogTitle></DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-5">
          <EmployeeIdentity form={form} setForm={setForm} />
          <BranchSelector branches={branches} value={branchSelection} onChange={setBranchSelection} />
          <AccessDesigner hoardings={hoardings} permissions={permissions} setPermissions={setPermissions} profile={profile} setProfile={setProfile} canEditPermissions={canEditPermissions} canAssignInventory={canAssignInventory} />
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => { reset(); onClose(); }}>Cancel</Button>
            <Button type="submit" disabled={saving} className="bg-blue-600 hover:bg-blue-700">{saving ? "Creating..." : "Create Employee"}</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function EditEmployeeDialog({ employee, hoardings, branches, requestedPermission, permissionRequest, onClose, onSaved }) {
  const { user } = useAuth();
  const [branchSelection, setBranchSelection] = useState(defaultBranchSelection(branches, employee));
  const [permissions, setPermissions] = useState({ ...DEFAULT_PERMISSIONS, ...(employee.permissions || {}) });
  const [profile, setProfile] = useState(profileFrom(employee.access_profile));
  const [status, setStatus] = useState(employee.status);
  const [saving, setSaving] = useState(false);
  const canEditPermissions = hasPlanFeature(user, PLAN_FEATURES.employeeAccess) && hasPermission(user, "edit_employee_permissions");
  const canAssignInventory = hasPlanFeature(user, PLAN_FEATURES.employeeAccess) && hasPermission(user, "assign_inventory_to_employees");
  const canDeactivate = hasPermission(user, "deactivate_employees");
  const isRequestedGranted = requestedPermission ? !!permissions[requestedPermission] : false;

  useEffect(() => {
    setBranchSelection(defaultBranchSelection(branches, employee));
  }, [branches, employee]);

  async function handleSave() {
    setSaving(true);
    try {
      const payload = {};
      if (canEditPermissions) {
        payload.branch_type = branchSelection.branch_type || "main";
        payload.branch_id = branchSelection.branch_id || null;
      }
      if (canEditPermissions) payload.permissions = withLegacyAliases(permissions);
      if (canAssignInventory) payload.access_profile = profile;
      if (canDeactivate) payload.status = status;
      await api.put(`/employees/${employee.id}`, payload);
      toast.success("Employee updated");
      onSaved();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to update employee");
    } finally {
      setSaving(false);
    }
  }

  async function handleApproveRequest() {
    if (!permissionRequest) return;
    setSaving(true);
    try {
      await api.post(`/permission-requests/${permissionRequest.id}/approve`);
      toast.success("Permission approved");
      onSaved();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to approve request");
    } finally {
      setSaving(false);
    }
  }

  return (
    <Dialog open onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="max-w-5xl max-h-[90vh] overflow-y-auto">
        <DialogHeader><DialogTitle>Edit access - {employee.name}</DialogTitle></DialogHeader>
        <div className="space-y-5">
          {permissionRequest && (
            <div className="rounded-lg border border-amber-200 bg-amber-50 p-4">
              <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                <div>
                  <p className="text-xs font-bold uppercase tracking-wide text-amber-700">Requested permission</p>
                  <p className="mt-1 text-sm font-semibold text-slate-950">{permissionLabel(permissionRequest.requested_permission)}</p>
                  <p className="mt-1 text-xs text-slate-600">{permissionRequest.note || "No note added"}</p>
                </div>
                <Button
                  onClick={handleApproveRequest}
                  disabled={saving || isRequestedGranted}
                  className="bg-amber-600 hover:bg-amber-700 text-white"
                >
                  {isRequestedGranted ? "Already enabled" : "Give permission"}
                </Button>
              </div>
            </div>
          )}
          <div className="flex items-center justify-between rounded-lg border border-slate-100 p-3">
            <div>
              <p className="text-sm font-medium text-slate-800">Account active</p>
              <p className="text-xs text-slate-400">Inactive employees cannot log in</p>
            </div>
            <Switch disabled={!canDeactivate} checked={status === "active"} onCheckedChange={(checked) => setStatus(checked ? "active" : "inactive")} />
          </div>
          <BranchSelector branches={branches} value={branchSelection} onChange={setBranchSelection} disabled={!canEditPermissions} />
          <AccessDesigner hoardings={hoardings} permissions={permissions} setPermissions={setPermissions} profile={profile} setProfile={setProfile} canEditPermissions={canEditPermissions} canAssignInventory={canAssignInventory} requestedPermission={requestedPermission} />
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={handleSave} disabled={saving} className="bg-blue-600 hover:bg-blue-700">{saving ? "Saving..." : "Save Changes"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function defaultBranchSelection(branches = [], employee = null) {
  const byID = employee?.branch_id ? branches.find((branch) => branch.id === employee.branch_id) : null;
  const byType = employee?.branch_type ? branches.find((branch) => branch.branch_type === employee.branch_type) : null;
  const main = branches.find((branch) => branch.branch_type === "main");
  const branch = byID || byType || main || branches[0];
  return {
    branch_id: branch?.id || employee?.branch_id || "",
    branch_type: branch?.branch_type || employee?.branch_type || "main",
  };
}

function BranchSelector({ branches = [], value, onChange, disabled = false }) {
  const options = branches.length > 0
    ? branches
    : [{ id: "main", name: "Main Branch", branch_type: "main" }];
  const selected = options.find((branch) => branch.id === value.branch_id)
    || options.find((branch) => branch.branch_type === value.branch_type)
    || options[0];

  function handleChange(branchID) {
    const branch = options.find((item) => item.id === branchID);
    if (!branch) return;
    onChange({ branch_id: branch.id === "main" && branches.length === 0 ? "" : branch.id, branch_type: branch.branch_type || "main" });
  }

  return (
    <div className="hx-employee-section hx-employee-branch">
      <div className="grid grid-cols-1 items-start gap-3 md:grid-cols-[0.8fr_2fr]">
        <div>
          <Label className="text-xs font-semibold uppercase tracking-wide text-slate-500">Branch access</Label>
          <p className="mt-1 text-xs leading-5 text-slate-400">
            Controls whether this employee belongs to Main, Local, or Agency branch data.
          </p>
        </div>
        <Select value={selected?.id || ""} onValueChange={handleChange} disabled={disabled}>
          <SelectTrigger>
            <SelectValue placeholder="Select branch" />
          </SelectTrigger>
          <SelectContent>
            {options.map((branch) => (
              <SelectItem key={branch.id} value={branch.id}>
                {branch.name || BRANCH_TYPE_LABELS[branch.branch_type] || "Branch"} - {BRANCH_TYPE_LABELS[branch.branch_type] || "Branch"}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
    </div>
  );
}

function EmployeeIdentity({ form, setForm }) {
  return (
    <div className="hx-employee-identity grid grid-cols-1 gap-3 sm:grid-cols-3">
      <Field label="Full name"><Input value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} placeholder="Jane Smith" required /></Field>
      <Field label="Email"><Input type="email" value={form.email} onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))} placeholder="jane@company.com" required /></Field>
      <Field label="Temporary password"><Input type="password" value={form.password} onChange={(e) => setForm((f) => ({ ...f, password: e.target.value }))} placeholder="Min. 6 characters" required minLength={6} /></Field>
    </div>
  );
}

function AccessDesigner({ hoardings, permissions, setPermissions, profile, setProfile, canEditPermissions = true, canAssignInventory = true, requestedPermission = "" }) {
  const [activeGroup, setActiveGroup] = useState("");
  const [query, setQuery] = useState("");
  const [detailPerm, setDetailPerm] = useState(null);
  const cities = useMemo(() => [...new Set(hoardings.map((h) => h.city).filter(Boolean))].sort(), [hoardings]);
  const areas = useMemo(() => {
    const seen = new Set();
    return hoardings
      .map((h) => ({ city: h.city, area: h.area }))
      .filter((x) => x.city && x.area)
      .filter((x) => {
        const key = `${x.city}|${x.area}`;
        if (seen.has(key)) return false;
        seen.add(key);
        return true;
      })
      .sort((a, b) => `${a.city} ${a.area}`.localeCompare(`${b.city} ${b.area}`));
  }, [hoardings]);
  const enabledCount = ALL_PERMISSIONS.filter((perm) => permissions[perm.key]).length;
  const enabledGroups = PERMISSION_GROUPS
    .map((group) => ({
      title: group.title,
      enabled: group.items.filter((perm) => permissions[perm.key]).length,
      total: group.items.length,
    }))
    .filter((group) => group.enabled > 0);
  const normalizedQuery = query.trim().toLowerCase();
  const displayedGroups = PERMISSION_GROUPS
    .filter((group) => !activeGroup || group.title === activeGroup)
    .map((group) => ({
      ...group,
      items: group.items.filter((perm) => {
        if (!normalizedQuery) return true;
        return [
          perm.label,
          perm.key,
          perm.help,
          ...(perm.affects || []),
        ].join(" ").toLowerCase().includes(normalizedQuery);
      }),
    }))
    .filter((group) => group.items.length > 0);

  function applyPreset(preset) {
    setPermissions({ ...DEFAULT_PERMISSIONS, ...Object.fromEntries(preset.permissions.map((key) => [key, true])) });
    setProfile((p) => ({ ...p, role_preset: preset.id, scope_type: preset.scope_type }));
  }

  function togglePermission(key) {
    setPermissions((prev) => ({ ...prev, [key]: !prev[key] }));
  }

  function clearPermissions() {
    setPermissions({ ...DEFAULT_PERMISSIONS });
  }

  function toggleCity(city) {
    setProfile((prev) => {
      const has = prev.cities.includes(city);
      return { ...prev, cities: has ? prev.cities.filter((x) => x !== city) : [...prev.cities, city] };
    });
  }

  function toggleArea(areaRef) {
    setProfile((prev) => {
      const key = `${areaRef.city}|${areaRef.area}`;
      const has = prev.areas.some((x) => `${x.city}|${x.area}` === key);
      return { ...prev, areas: has ? prev.areas.filter((x) => `${x.city}|${x.area}` !== key) : [...prev.areas, areaRef] };
    });
  }

  function toggleHoarding(id) {
    setProfile((prev) => {
      const has = prev.hoarding_ids.includes(id);
      return { ...prev, hoarding_ids: has ? prev.hoarding_ids.filter((x) => x !== id) : [...prev.hoarding_ids, id] };
    });
  }

  return (
    <div className="hx-access-designer space-y-4">
      {canEditPermissions && (
        <div className="hx-employee-section hx-access-summary">
          <div className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
            <div>
              <Label className="text-xs font-semibold uppercase tracking-wide text-slate-500">Smart access setup</Label>
              <p className="mt-1 text-xs leading-5 text-slate-500">
                Start from a role preset, then adjust the exact toggles below. Nothing is hidden; this just keeps the choices readable.
              </p>
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <span className="rounded-full bg-blue-50 px-2.5 py-1 text-xs font-bold text-blue-700">
                {enabledCount}/{ALL_PERMISSIONS.length} enabled
              </span>
              <Button type="button" size="sm" variant="outline" onClick={clearPermissions}>
                Clear
              </Button>
            </div>
          </div>

          {canAssignInventory && (
            <div className="mt-3 grid grid-cols-1 gap-2 sm:grid-cols-2 xl:grid-cols-4">
              {ROLE_PRESETS.map((preset) => {
                const active = profile.role_preset === preset.id;
                return (
                  <button
                    key={preset.id}
                    type="button"
                    onClick={() => applyPreset(preset)}
                    className={`hx-role-preset ${active ? "is-active" : ""}`}
                  >
                    <div className="flex items-center justify-between gap-2">
                      <div className="font-semibold text-sm text-slate-900">{preset.label}</div>
                      <span className={`rounded-full px-1.5 py-0.5 text-[10px] font-bold ${active ? "bg-blue-100 text-blue-700" : "bg-slate-100 text-slate-500"}`}>
                        {preset.permissions.length}
                      </span>
                    </div>
                    <div className="mt-1 text-xs leading-5 text-slate-500">{preset.desc}</div>
                  </button>
                );
              })}
            </div>
          )}

          <div className="mt-3 grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
            {enabledGroups.length === 0 ? (
              <div className="hx-enabled-chip">No permissions selected yet.</div>
            ) : enabledGroups.map((group) => (
              <div key={group.title} className="hx-enabled-chip">
                <span className="font-semibold text-slate-800">{group.title}</span>
                <span className="ml-2 text-slate-500">{group.enabled}/{group.total}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
        {canAssignInventory && <div className="hx-employee-section hx-assignment-scope space-y-3 lg:col-span-1">
          <div>
            <Label className="text-xs font-semibold uppercase tracking-wide text-slate-500">Assignment scope</Label>
            <p className="mt-1 text-xs text-slate-400">Decides which inventory or market this employee should work on.</p>
          </div>
          {[
            ["all_inventory", "All inventory"],
            ["assigned_cities", "Specific cities"],
            ["assigned_areas", "Specific city areas"],
            ["assigned_hoardings", "Specific hoardings"],
          ].map(([value, label]) => (
            <label key={value} className="hx-scope-option">
              <input type="radio" checked={profile.scope_type === value} onChange={() => setProfile((p) => ({ ...p, scope_type: value }))} />
              {label}
            </label>
          ))}

          {profile.scope_type === "assigned_cities" && <Checklist icon={MapPin} items={cities} selected={profile.cities} onToggle={toggleCity} empty="No cities found yet." />}
          {profile.scope_type === "assigned_areas" && (
            <div className="hx-employee-checklist max-h-56 overflow-y-auto">
              {areas.map((a) => {
                const key = `${a.city}|${a.area}`;
                const checked = profile.areas.some((x) => `${x.city}|${x.area}` === key);
                return (
                  <label key={key} className="hx-check-row">
                    <input type="checkbox" checked={checked} onChange={() => toggleArea(a)} />
                    <span>{a.area}</span>
                    <span className="text-xs text-slate-400">{a.city}</span>
                  </label>
                );
              })}
              {areas.length === 0 && <div className="p-3 text-xs text-slate-400">No areas found yet.</div>}
            </div>
          )}
          {profile.scope_type === "assigned_hoardings" && (
            <div className="hx-employee-checklist max-h-64 overflow-y-auto">
              {hoardings.map((h) => (
                <label key={h.id} className="hx-check-row items-start">
                  <input type="checkbox" checked={profile.hoarding_ids.includes(h.id)} onChange={() => toggleHoarding(h.id)} className="mt-1" />
                  <span className="min-w-0">
                    <span className="block truncate font-medium text-slate-700">{h.name}</span>
                    <span className="block truncate text-xs text-slate-400">{h.hoarding_id} - {h.area}, {h.city}</span>
                  </span>
                </label>
              ))}
              {hoardings.length === 0 && <div className="p-3 text-xs text-slate-400">No hoardings found yet.</div>}
            </div>
          )}
        </div>}

        {canEditPermissions && <div className="space-y-3 lg:col-span-2">
          <div className="hx-employee-section hx-permission-toolbar">
            <div className="flex flex-col gap-3 xl:flex-row xl:items-center xl:justify-between">
              <div className="flex items-center gap-2 text-sm font-semibold text-slate-900">
                <SlidersHorizontal size={16} className="text-blue-500" /> Fine-grained permissions
              </div>
              <div className="relative w-full xl:w-72">
                <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                <Input
                  value={query}
                  onChange={(event) => setQuery(event.target.value)}
                  placeholder="Search permissions or pages"
                  className="pl-8"
                />
              </div>
            </div>
            <div className="mt-3 flex gap-2 overflow-x-auto pb-1">
              <button
                type="button"
                onClick={() => setActiveGroup("")}
                className={`hx-permission-filter ${!activeGroup ? "is-active" : ""}`}
              >
                All
              </button>
              {PERMISSION_GROUPS.map((group) => {
                const active = activeGroup === group.title;
                const enabled = group.items.filter((perm) => permissions[perm.key]).length;
                return (
                  <button
                    key={group.title}
                    type="button"
                    onClick={() => setActiveGroup(group.title)}
                    className={`hx-permission-filter ${active ? "is-active" : ""}`}
                  >
                    {group.title}
                    {enabled > 0 && <span className="ml-1 text-[10px] text-slate-400">{enabled}</span>}
                  </button>
                );
              })}
            </div>
          </div>

          {detailPerm && <PermissionImpactPanel permission={detailPerm} onClose={() => setDetailPerm(null)} />}

          {displayedGroups.map((group) => (
            <div key={group.title} className="hx-permission-group">
              <div className="hx-permission-group-head">
                <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-slate-500">
                  <Building2 size={13} /> {group.title}
                </div>
                <div className="text-xs text-slate-500">{group.summary}</div>
              </div>
              <div className="grid grid-cols-1 divide-y divide-slate-50">
                {group.items.map((perm) => {
                  const requested = requestedPermission === perm.key;
                  return (
                    <div key={perm.key} className={`hx-permission-row ${requested ? "is-requested" : ""}`}>
                      <div className="min-w-0">
                        <div className="flex flex-wrap items-center gap-2">
                          <span className={`text-sm ${requested ? "font-semibold text-amber-900" : "font-semibold text-slate-800"}`}>
                            {perm.label}
                          </span>
                          {requested && <span className="rounded-full bg-amber-100 px-1.5 py-0.5 text-[10px] font-bold uppercase text-amber-700">requested</span>}
                        </div>
                        <div className="mt-1 text-xs leading-5 text-slate-500">{perm.help}</div>
                        <div className="mt-2 flex flex-wrap gap-1">
                          {(perm.affects || []).slice(0, 4).map((item) => (
                            <span key={item} className="rounded bg-slate-100 px-1.5 py-0.5 text-[10px] font-semibold text-slate-600">{item}</span>
                          ))}
                        </div>
                      </div>
                      <div className="flex shrink-0 items-center gap-2">
                        <button
                          type="button"
                          onClick={() => setDetailPerm(perm)}
                          className="rounded-md p-1.5 text-slate-400 hover:bg-slate-100 hover:text-blue-600"
                          aria-label={`Show impact for ${perm.label}`}
                        >
                          <HelpCircle size={15} />
                        </button>
                        <Switch checked={!!permissions[perm.key]} onCheckedChange={() => togglePermission(perm.key)} />
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
          {displayedGroups.length === 0 && (
            <div className="rounded-lg border border-slate-200 bg-white py-10 text-center text-sm text-slate-400">
              No permissions match your search.
            </div>
          )}
        </div>}
      </div>
    </div>
  );
}

function PermissionImpactPanel({ permission, onClose }) {
  return (
    <div className="rounded-lg border border-blue-200 bg-blue-50 p-4">
      <div className="flex items-start justify-between gap-3">
        <div>
          <p className="text-xs font-bold uppercase tracking-wide text-blue-700">Permission impact</p>
          <h4 className="mt-1 text-sm font-bold text-slate-950">{permission.label}</h4>
          <p className="mt-1 text-sm leading-6 text-slate-700">{permission.help}</p>
        </div>
        <button type="button" onClick={onClose} className="rounded-md px-2 py-1 text-xs font-semibold text-blue-700 hover:bg-white">
          Close
        </button>
      </div>
      <div className="mt-3">
        <div className="text-xs font-semibold uppercase tracking-wide text-blue-700">Affected pages/components</div>
        <div className="mt-2 flex flex-wrap gap-2">
          {(permission.affects || []).map((item) => (
            <span key={item} className="rounded-md bg-white px-2 py-1 text-xs font-semibold text-slate-700 shadow-sm">{item}</span>
          ))}
        </div>
      </div>
      <div className="mt-3 rounded-md bg-white/70 px-3 py-2 font-mono text-[11px] text-slate-500">{permission.key}</div>
    </div>
  );
}

function Checklist({ icon: Icon, items, selected, onToggle, empty }) {
  return (
    <div className="hx-employee-checklist max-h-56 overflow-y-auto">
      {items.map((item) => (
        <label key={item} className="hx-check-row">
          <input type="checkbox" checked={selected.includes(item)} onChange={() => onToggle(item)} />
          {Icon && <Icon size={13} className="text-slate-400" />}
          {item}
        </label>
      ))}
      {items.length === 0 && <div className="p-3 text-xs text-slate-400">{empty}</div>}
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div className="space-y-1">
      <Label>{label}</Label>
      {children}
    </div>
  );
}
